import {
  isSendablePaymentAccountType,
  PAYMENT_ACCOUNT_TYPE_KLEAR_FINANCIAL_ACCOUNT,
  PAYMENT_NETWORK_KLEAR_TO_KLEAR
} from '@klear/payment/constants'
import type { InvoiceOfferPaymentMetaWire } from '@klear/invoice-offer/invoice-offer.wire'
import isInteger from 'lodash/isInteger'
import isNumber from 'lodash/isNumber'
import isPlainObject from 'lodash/isPlainObject'

type PaymentAccountDetails = InvoiceOfferPaymentMetaWire['traderAccountDetails']

function isPositiveInteger(value: unknown): value is number {
  return isNumber(value) && isInteger(value) && value > 0
}

export function isInvoiceOfferPaymentAccountDetails(value: unknown): value is NonNullable<PaymentAccountDetails> {
  if (!isPlainObject(value)) return false
  const details = value as Record<string, unknown>
  return (
    isSendablePaymentAccountType(details.accountType) &&
    isPositiveInteger(details.accountId) &&
    isPositiveInteger(details.organizationId)
  )
}

export function invoiceOfferHasValidPaymentMeta(paymentMeta: InvoiceOfferPaymentMetaWire | undefined): boolean {
  if (!paymentMeta?.paymentNetwork) return false
  return (
    isInvoiceOfferPaymentAccountDetails(paymentMeta.traderAccountDetails) &&
    isInvoiceOfferPaymentAccountDetails(paymentMeta.klearAccountDetails)
  )
}

export type InvoiceOfferFunderKlearAccount = {
  klearBankAccountId?: number | null
  organizationId?: number | null
  organizationName?: string | null
}

export function invoiceOfferKlearAccountDetails(
  funder: InvoiceOfferFunderKlearAccount | undefined
): NonNullable<InvoiceOfferPaymentMetaWire['klearAccountDetails']> | null {
  if (funder?.klearBankAccountId == null || funder.organizationId == null) return null
  return {
    accountType: PAYMENT_ACCOUNT_TYPE_KLEAR_FINANCIAL_ACCOUNT,
    accountId: funder.klearBankAccountId,
    organizationId: funder.organizationId,
    orgName: funder.organizationName ?? undefined
  }
}

export function invoiceOfferKlearToKlearPaymentMeta(
  traderAccount: { id?: number | null; organizationId: number },
  klearAccountDetails: NonNullable<InvoiceOfferPaymentMetaWire['klearAccountDetails']>
): InvoiceOfferPaymentMetaWire | undefined {
  if (traderAccount.id == null) return undefined
  return {
    paymentNetwork: PAYMENT_NETWORK_KLEAR_TO_KLEAR,
    traderAccountDetails: {
      accountType: PAYMENT_ACCOUNT_TYPE_KLEAR_FINANCIAL_ACCOUNT,
      accountId: traderAccount.id,
      organizationId: traderAccount.organizationId
    },
    klearAccountDetails
  }
}

export function invoiceOfferFundingSpvLabel(paymentMeta: InvoiceOfferPaymentMetaWire | undefined): string {
  const d = paymentMeta?.klearAccountDetails
  if (!d || d.organizationId <= 0) return '—'
  const name = d.orgName?.trim()
  return name && name.length > 0 ? name : `Org #${d.organizationId}`
}

export type InvoiceOfferFundingSpvFunderLookup = {
  organizationName: string
}

export const INVOICE_OFFER_FUNDER_NAME_UNKNOWN = 'Unknown'

export function invoiceOfferFunderNameFromPricingFunderId(
  funderId: number | undefined,
  funderByFunderId: ReadonlyMap<number, InvoiceOfferFundingSpvFunderLookup>
): string {
  if (funderId == null || funderId <= 0) return INVOICE_OFFER_FUNDER_NAME_UNKNOWN
  const name = funderByFunderId.get(funderId)?.organizationName?.trim()
  return name && name.length > 0 ? name : INVOICE_OFFER_FUNDER_NAME_UNKNOWN
}
